import type { CmsSelection } from "../types";

export class CmsLayerTree {
    readonly #host: HTMLElement | null;

    constructor(private readonly root: HTMLElement) {
        this.#host = root.querySelector<HTMLElement>("[data-cms-layer-host]");
    }

    init(): void {
        this.refresh();
        this.root.addEventListener("cms:canvas-updated", () => this.refresh());
        this.root.addEventListener("cms:selection-changed", (event) => {
            const detail = (event as CustomEvent<CmsSelection>).detail;
            this.#highlight(detail.type, detail.id);
        });
        this.#host?.addEventListener("click", (event) => {
            const item = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-cms-layer-select]");
            if (!item) return;
            event.preventDefault();
            this.root.dispatchEvent(new CustomEvent("cms:select-request", {
                bubbles: true,
                detail: {
                    type: item.dataset.cmsLayerType ?? "",
                    id: item.dataset.cmsLayerId ?? "",
                },
            }));
        });
    }

    refresh(): void {
        if (!this.#host) return;
        const sections = Array.from(this.root.querySelectorAll<HTMLElement>("[data-cms-editable-type='section']"));
        if (sections.length === 0) {
            this.#host.innerHTML = '<p class="admin-cms-builder__empty">선택된 페이지의 섹션이 없습니다.</p>';
            return;
        }
        const list = document.createElement("ol");
        list.className = "admin-cms-layer-tree";
        sections.forEach((section, index) => {
            const sectionItem = this.#item(section, "section", index + 1);
            const elements = Array.from(section.querySelectorAll<HTMLElement>("[data-cms-editable-type='element']"));
            if (elements.length > 0) {
                const childList = document.createElement("ol");
                childList.className = "admin-cms-layer-tree__children";
                elements.forEach((element, elementIndex) => childList.appendChild(this.#item(element, "element", elementIndex + 1)));
                sectionItem.appendChild(childList);
            }
            list.appendChild(sectionItem);
        });
        this.#host.replaceChildren(list);
    }

    #item(el: HTMLElement, type: "section" | "element", index: number): HTMLLIElement {
        const li = document.createElement("li");
        li.className = `admin-cms-layer-tree__item admin-cms-layer-tree__item--${type}`;
        const button = document.createElement("button");
        button.type = "button";
        button.className = "admin-cms-layer-tree__button";
        button.dataset.cmsLayerSelect = "1";
        button.dataset.cmsLayerType = type;
        button.dataset.cmsLayerId = el.dataset.cmsEditableId ?? "";
        const label = type === "section" ? "Frame" : "Layer";
        const badgeClass = type === "section" ? "admin-cms-layer-tree__badge--frame" : "admin-cms-layer-tree__badge--layer";
        button.innerHTML = `
            <span class="admin-cms-layer-tree__badge ${badgeClass}">${label}</span>
            <span class="admin-cms-layer-tree__main">
                <span class="admin-cms-layer-tree__name">${label} ${index}</span>
            </span>
            <small>${this.#escape(el.dataset.title || el.dataset.typeLabel || type)}</small>
            <span class="admin-cms-layer-tree__state" aria-hidden="true">
                <i class="fa-regular fa-eye"></i>
            </span>
        `;
        li.appendChild(button);
        return li;
    }

    #highlight(type: string, id: string): void {
        this.#host?.querySelectorAll<HTMLElement>("[data-cms-layer-select]").forEach((item) => {
            const active = item.dataset.cmsLayerType === type && item.dataset.cmsLayerId === id;
            item.classList.toggle("is-active", active);
        });
    }

    #escape(value: string): string {
        return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    }
}
